import type { UIMessage } from "ai";
import { getToolOrDynamicToolName, isToolOrDynamicToolUIPart } from "ai";

type AssistantSourcePart = UIMessage["parts"][number];

// The agent's documentation citations are not render parts — they arrive in their own channel and
// render as chips below the message. See utils/docSources.ts. (RTLCA-4937)

// render_* tools only draw UI (charts, tables, metrics) and never change project data. Every
// other tool the agent can call is a data/MCP tool that may mutate the model or the building
// component inventory. The agent's write-tool catalogue lives in the external si-agent service
// and can't be reliably enumerated here, so we key off this one stable convention (the same
// split AssistantMessage renders on) instead of a write-verb allowlist: anything that isn't a
// render_* tool is treated as data-affecting for the dashboard refresh. (RTLCA-4865)
export function isUiRenderToolName(toolName: string): boolean {
  return toolName.startsWith("render_");
}

export type AssistantRenderPart =
  | {
      key: string;
      type: "text";
      text: string;
    }
  | {
      key: string;
      type: "reasoning";
      reasoning: string;
    }
  | {
      key: string;
      type: "tool";
      toolCallId: string;
      toolName: string;
      input: unknown;
      result: unknown;
      isLoading: boolean;
      isError: boolean;
    }
  | {
      key: string;
      type: "approval";
      toolCallId: string;
      toolName: string;
      input: unknown;
      approvalId: string;
    };

function isTextPart(part: AssistantSourcePart): part is Extract<AssistantSourcePart, { type: "text" }> {
  return part.type === "text";
}

function isReasoningPart(part: AssistantSourcePart): part is Extract<AssistantSourcePart, { type: "reasoning" }> {
  return part.type === "reasoning";
}

export function getMessageText(message: Pick<UIMessage, "parts">): string {
  return message.parts
    .filter(isTextPart)
    .map((part) => part.text)
    .join("");
}

export function getAssistantRenderParts(message: Pick<UIMessage, "parts">): AssistantRenderPart[] {
  return message.parts.flatMap((part, index): AssistantRenderPart[] => {
    if (isTextPart(part)) {
      const text = part.text.trim();
      return text ? [{ key: `text-${index}`, type: "text", text }] : [];
    }

    if (isReasoningPart(part)) {
      return part.text ? [{ key: `reasoning-${index}`, type: "reasoning", reasoning: part.text }] : [];
    }

    // MCP tools arrive as dynamic-tool parts, the backend's render_* tools as tool-* parts.
    if (isToolOrDynamicToolUIPart(part)) {
      const toolName = getToolOrDynamicToolName(part);

      // A write/destructive tool the agent wants to run, paused for the user to allow or deny.
      // Rendered as a ToolApprovalCard until the user responds; once the state moves on, this
      // branch stops matching and the part falls through to the regular tool block below.
      if (part.state === "approval-requested") {
        return [
          {
            key: part.toolCallId ?? `approval-${index}-${toolName}`,
            type: "approval",
            toolCallId: part.toolCallId,
            toolName,
            input: part.input,
            approvalId: part.approval.id,
          },
        ];
      }

      return [
        {
          key: part.toolCallId ?? `tool-${index}-${toolName}-${part.state}`,
          type: "tool",
          toolCallId: part.toolCallId,
          toolName,
          input: part.input,
          result: part.state === "output-available" ? part.output : null,
          // "approval-responded" is the approved-and-resuming window before the tool output
          // arrives — show it as in-progress rather than briefly flashing a success check.
          isLoading: part.state === "input-streaming" || part.state === "input-available" || part.state === "approval-responded",
          // A denied tool never ran; flag it like an error so it isn't shown as a success.
          isError: part.state === "output-error" || part.state === "output-denied",
        },
      ];
    }

    return [];
  });
}
